import React, { useState, useEffect } from 'react';
import { obtenerConfigTurnero, actualizarConfigTurnero, importarTurnosCsv,
    listarDiasCerradosPropios, marcarDiaNoLaborable, reabrirDiaNoLaborable } from '../services/turneroService';
import PageHeader from '../components/ui/PageHeader';

// Mismo criterio que java.time.DayOfWeek.getValue() en el backend: 1=lunes .. 7=domingo.
const DIAS = [
    { diaSemana: 1, label: 'Lunes' },
    { diaSemana: 2, label: 'Martes' },
    { diaSemana: 3, label: 'Miércoles' },
    { diaSemana: 4, label: 'Jueves' },
    { diaSemana: 5, label: 'Viernes' },
    { diaSemana: 6, label: 'Sábado' },
    { diaSemana: 7, label: 'Domingo' },
];

const DURACIONES = [
    { value: 15,  label: '15 minutos' },
    { value: 20,  label: '20 minutos' },
    { value: 30,  label: '30 minutos' },
    { value: 45,  label: '45 minutos' },
    { value: 60,  label: '1 hora'     },
];

const MAX_TURNOS_OPCIONES = [1, 2, 3, 4, 5];

// Mismo algoritmo que TurnoRecepcionService.generarBloquesHorarios() / TurneroPage.js — para
// que el conteo de la preview coincida con los bloques que se van a ofrecer de verdad ese
// día, incluyendo el corte de su propio horario de almuerzo cuando está configurado.
function contarBloquesHorarios(dia, duracionMinutos) {
    if (!dia?.activo || !dia?.horaInicio || !dia?.horaFin || !(duracionMinutos > 0)) return 0;
    const [hI, mI] = dia.horaInicio.split(':').map(Number);
    const [hF, mF] = dia.horaFin.split(':').map(Number);
    let mins = hI * 60 + mI;
    const finMins = hF * 60 + mF;
    if (finMins < mins) return 0;
    let almuerzoInicioMins = null;
    let almuerzoFinMins = null;
    if (dia.horarioAlmuerzoInicio && dia.horarioAlmuerzoFin) {
        const [hAI, mAI] = dia.horarioAlmuerzoInicio.split(':').map(Number);
        const [hAF, mAF] = dia.horarioAlmuerzoFin.split(':').map(Number);
        almuerzoInicioMins = hAI * 60 + mAI;
        almuerzoFinMins = hAF * 60 + mAF;
    }
    let total = 0;
    while (mins <= finMins) {
        const enAlmuerzo = almuerzoInicioMins !== null && mins >= almuerzoInicioMins && mins < almuerzoFinMins;
        if (!enAlmuerzo) total += 1;
        mins += duracionMinutos;
    }
    return total;
}

function validarDia(dia, etiqueta) {
    if (!dia.horaInicio || !dia.horaFin) {
        return `${etiqueta}: debe ingresar hora de inicio y de fin.`;
    }
    if (dia.horaFin <= dia.horaInicio) {
        return `${etiqueta}: la hora de fin debe ser posterior a la hora de inicio.`;
    }
    const tieneAlmuerzoInicio = !!dia.horarioAlmuerzoInicio;
    const tieneAlmuerzoFin = !!dia.horarioAlmuerzoFin;
    if (tieneAlmuerzoInicio !== tieneAlmuerzoFin) {
        return `${etiqueta}: cargá el inicio y el fin del horario de almuerzo juntos, o dejá los dos vacíos.`;
    }
    if (tieneAlmuerzoInicio && dia.horarioAlmuerzoFin <= dia.horarioAlmuerzoInicio) {
        return `${etiqueta}: el fin del horario de almuerzo debe ser posterior al inicio.`;
    }
    if (tieneAlmuerzoInicio && (dia.horarioAlmuerzoInicio < dia.horaInicio || dia.horarioAlmuerzoFin > dia.horaFin)) {
        return `${etiqueta}: el horario de almuerzo debe estar dentro del horario de atención.`;
    }
    return null;
}

export default function ConfiguracionTurneroPage() {
    const [config, setConfig] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [exito, setExito] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        obtenerConfigTurnero()
            .then(setConfig)
            .catch(() => setError('No se pudo cargar la configuración.'))
            .finally(() => setCargando(false));
    }, []);

    const handleDiaCampo = (diaSemana, campo, valor) => {
        setConfig(c => ({
            ...c,
            horariosPorDia: c.horariosPorDia.map(d => d.diaSemana === diaSemana ? { ...d, [campo]: valor } : d),
        }));
        setExito(false);
    };

    const handleToggleDia = (diaSemana) => {
        handleDiaCampo(diaSemana, 'activo', !config.horariosPorDia.find(d => d.diaSemana === diaSemana)?.activo);
    };

    const handleCampo = (key, valor) => {
        setConfig(c => ({ ...c, [key]: valor }));
        setExito(false);
    };

    const handleGuardar = async () => {
        setError('');
        setExito(false);
        const diasActivos = config.horariosPorDia.filter(d => d.activo);
        if (diasActivos.length === 0) {
            setError('Debe seleccionar al menos un día de atención.');
            return;
        }
        for (const dia of diasActivos) {
            const etiqueta = DIAS.find(d => d.diaSemana === dia.diaSemana)?.label || `Día ${dia.diaSemana}`;
            const errorDia = validarDia(dia, etiqueta);
            if (errorDia) {
                setError(errorDia);
                return;
            }
        }
        setGuardando(true);
        try {
            const actualizado = await actualizarConfigTurnero(config);
            setConfig(actualizado);
            setExito(true);
        } catch (e) {
            setError(e?.response?.data?.mensaje || 'Error al guardar la configuración.');
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    return (
        <div className="area-recepcion" style={{ padding: '24px', minHeight: '100vh', background: 'var(--bg-page)' }}>
            <div style={{ maxWidth: 960, margin: '0 auto' }}>
                <PageHeader
                    variante="plano"
                    icono="gear-fill"
                    titulo="Configuración del Turnero"
                    descripcion="Estos valores determinan los días de atención, el horario y la duración de cada turno."
                />

                {error && <div className="alert alert-danger">{error}</div>}
                {exito && (
                    <div className="alert alert-success d-flex align-items-center gap-2">
                        <i className="bi bi-check-circle-fill" />
                        Configuración guardada correctamente.
                    </div>
                )}

                {config && (
                    <div style={{ background: '#fff', borderRadius: 12, boxShadow: 'var(--shadow-sm)', padding: 28 }}>
                        {/* Horario por día */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, display: 'block' }}>
                                <i className="bi bi-calendar-week me-2 text-primary" />
                                Días y horario de atención
                            </label>
                            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.85rem' }}>
                                Cada día puede tener su propio horario y su propio corte de almuerzo — por ejemplo, atender hasta las 15:00 los lunes y hasta las 12:00 los viernes.
                            </p>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table" style={{ fontSize: '0.88rem', marginBottom: 0 }}>
                                    <thead>
                                        <tr style={{ color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase' }}>
                                            <th style={{ minWidth: 110 }}>Día</th>
                                            <th style={{ minWidth: 130 }}>Desde</th>
                                            <th style={{ minWidth: 130 }}>Hasta</th>
                                            <th style={{ minWidth: 130 }}>Almuerzo desde</th>
                                            <th style={{ minWidth: 130 }}>Almuerzo hasta</th>
                                            <th style={{ minWidth: 90, textAlign: 'right' }}>Turnos/día</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {DIAS.map(({ diaSemana, label }) => {
                                            const dia = config.horariosPorDia.find(d => d.diaSemana === diaSemana) || {};
                                            const activo = !!dia.activo;
                                            return (
                                                <tr key={diaSemana} style={{ opacity: activo ? 1 : 0.55 }}>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleDia(diaSemana)}
                                                            style={{
                                                                padding: '6px 14px',
                                                                borderRadius: 8,
                                                                border: `2px solid ${activo ? '#3b82f6' : '#cbd5e1'}`,
                                                                background: activo ? '#eff6ff' : '#f8fafc',
                                                                color: activo ? '#1e40af' : '#64748b',
                                                                fontWeight: activo ? 700 : 400,
                                                                cursor: 'pointer',
                                                                fontSize: '0.85rem',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {activo && <i className="bi bi-check2 me-1" />}
                                                            {label}
                                                        </button>
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <input
                                                            type="time"
                                                            className="form-control form-control-sm"
                                                            disabled={!activo}
                                                            value={dia.horaInicio || ''}
                                                            onChange={e => handleDiaCampo(diaSemana, 'horaInicio', e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <input
                                                            type="time"
                                                            className="form-control form-control-sm"
                                                            disabled={!activo}
                                                            value={dia.horaFin || ''}
                                                            onChange={e => handleDiaCampo(diaSemana, 'horaFin', e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <input
                                                            type="time"
                                                            className="form-control form-control-sm"
                                                            disabled={!activo}
                                                            value={dia.horarioAlmuerzoInicio || ''}
                                                            onChange={e => handleDiaCampo(diaSemana, 'horarioAlmuerzoInicio', e.target.value || null)}
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <input
                                                            type="time"
                                                            className="form-control form-control-sm"
                                                            disabled={!activo}
                                                            value={dia.horarioAlmuerzoFin || ''}
                                                            onChange={e => handleDiaCampo(diaSemana, 'horarioAlmuerzoFin', e.target.value || null)}
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>
                                                        {activo ? contarBloquesHorarios(dia, config.duracionMinutos) : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />

                        {/* Duración */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 700, color: '#1e293b', marginBottom: 12, display: 'block' }}>
                                <i className="bi bi-hourglass-split me-2 text-primary" />
                                Duración de cada turno
                            </label>
                            <div className="d-flex gap-2 flex-wrap">
                                {DURACIONES.map(d => (
                                    <button
                                        key={d.value}
                                        type="button"
                                        onClick={() => handleCampo('duracionMinutos', d.value)}
                                        style={{
                                            padding: '8px 18px',
                                            borderRadius: 8,
                                            border: `2px solid ${config.duracionMinutos === d.value ? '#3b82f6' : '#cbd5e1'}`,
                                            background: config.duracionMinutos === d.value ? '#eff6ff' : '#f8fafc',
                                            color: config.duracionMinutos === d.value ? '#1e40af' : '#64748b',
                                            fontWeight: config.duracionMinutos === d.value ? 700 : 400,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />

                        {/* Máximo de turnos por horario */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, display: 'block' }}>
                                <i className="bi bi-people-fill me-2 text-primary" />
                                Máximo de turnos por horario
                            </label>
                            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.85rem' }}>
                                Cuántos turnos se pueden agendar en el mismo horario antes de tener que ofrecerlo como sobreturno.
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                {MAX_TURNOS_OPCIONES.map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => handleCampo('maxTurnosPorHorario', n)}
                                        style={{
                                            padding: '8px 18px',
                                            borderRadius: 8,
                                            border: `2px solid ${config.maxTurnosPorHorario === n ? '#3b82f6' : '#cbd5e1'}`,
                                            background: config.maxTurnosPorHorario === n ? '#eff6ff' : '#f8fafc',
                                            color: config.maxTurnosPorHorario === n ? '#1e40af' : '#64748b',
                                            fontWeight: config.maxTurnosPorHorario === n ? 700 : 400,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />

                        {/* Cupo reservado para el chatbot externo */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, display: 'block' }}>
                                <i className="bi bi-robot me-2 text-primary" />
                                Cupo reservado para el chatbot
                            </label>
                            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.85rem' }}>
                                Lugares de cada horario que quedan guardados para el chatbot externo hasta que los use. Recepción puede ocupar el resto a mano. 0 = sin reserva.
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                {Array.from({ length: config.maxTurnosPorHorario }, (_, n) => n).map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => handleCampo('cupoReservadoChatbot', n)}
                                        style={{
                                            padding: '8px 18px',
                                            borderRadius: 8,
                                            border: `2px solid ${config.cupoReservadoChatbot === n ? '#3b82f6' : '#cbd5e1'}`,
                                            background: config.cupoReservadoChatbot === n ? '#eff6ff' : '#f8fafc',
                                            color: config.cupoReservadoChatbot === n ? '#1e40af' : '#64748b',
                                            fontWeight: config.cupoReservadoChatbot === n ? 700 : 400,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />

                        {/* Anticipación de la citación al estudio en los turnos ANSES */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, display: 'block' }}>
                                <i className="bi bi-building me-2 text-primary" />
                                Anticipación de los turnos ANSES
                            </label>
                            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.85rem' }}>
                                Minutos antes del turno en ANSES con los que se cita al cliente al estudio. Cada turno
                                puede usar un valor propio si el horario no es puntual.
                            </p>
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max="480"
                                    step="15"
                                    value={config.offsetTurnoAnsesMinutos ?? 60}
                                    onChange={(e) => handleCampo('offsetTurnoAnsesMinutos', Number(e.target.value))}
                                    style={{ width: 110,
                                        padding: '8px 12px',
                                        borderRadius: 8,
                                        border: '2px solid #cbd5e1' }}
                                />
                                <span style={{ color: '#64748b', fontSize: '0.9rem' }}>minutos</span>
                            </div>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={handleGuardar}
                            disabled={guardando}
                            style={{ minWidth: 160, fontWeight: 600 }}
                        >
                            {guardando
                                ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</>
                                : <><i className="bi bi-check-lg me-2" />Guardar configuración</>
                            }
                        </button>
                    </div>
                )}

                <PanelDiasNoLaborables />

                <PanelImportacionTurnos />
            </div>
        </div>
    );
}

/**
 * Días sueltos en los que el turnero no da turnos.
 *
 * Los feriados nacionales NO se cargan acá: ya vienen de la agenda (los importa la API) y el
 * turnero los cierra solo. Esta lista es para lo que la API no trae —un feriado provincial, como
 * el 30/09 en Córdoba— o para un día que el estudio decide no atender: mudanza, capacitación.
 *
 * Cerrar un día acá NO corre ningún plazo de vencimientos ni apelaciones (eso solo lo mueven los
 * feriados de la agenda) y NO toca los turnos que ya estaban cargados: se avisan a mano.
 */
function PanelDiasNoLaborables() {
    const [dias, setDias] = useState([]);
    const [fecha, setFecha] = useState('');
    const [motivo, setMotivo] = useState('');
    const [guardandoDia, setGuardandoDia] = useState(false);
    const [errorDia, setErrorDia] = useState('');
    const [avisoTurnos, setAvisoTurnos] = useState('');

    const cargar = () => listarDiasCerradosPropios().then(setDias).catch(() => setDias([]));

    useEffect(() => { cargar(); }, []);

    const agregar = async () => {
        if (!fecha || !motivo.trim()) {
            setErrorDia('Completá la fecha y el motivo.');
            return;
        }
        setGuardandoDia(true);
        setErrorDia('');
        setAvisoTurnos('');
        try {
            const creado = await marcarDiaNoLaborable(fecha, motivo.trim());
            // Marcar el día no cancela nada: si había turnos, hay que llamar a esa gente.
            if (creado?.turnosAgendados > 0) {
                setAvisoTurnos(`Ese día ya tenía ${creado.turnosAgendados} turno(s) agendado(s). No se cancelaron: hay que avisarles y reprogramarlos a mano.`);
            }
            setFecha('');
            setMotivo('');
            await cargar();
        } catch (e) {
            setErrorDia(e?.response?.data?.mensaje || 'No se pudo marcar el día.');
        } finally {
            setGuardandoDia(false);
        }
    };

    const reabrir = async (id) => {
        try {
            await reabrirDiaNoLaborable(id);
            await cargar();
        } catch (e) {
            setErrorDia(e?.response?.data?.mensaje || 'No se pudo reabrir el día.');
        }
    };

    return (
        <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 4px', fontWeight: 700, color: '#1e293b', fontSize: '1.15rem' }}>
                <i className="bi bi-slash-circle me-2" />Días en los que no se atiende
            </h3>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.9rem' }}>
                Los feriados nacionales ya cierran el turnero solos (salen de la agenda). Acá se agregan los que
                la API no trae —un feriado provincial— o los días que el estudio decide no atender. El bot no va a
                ofrecer turnos esos días; desde el mostrador se puede cargar igual, confirmando.
            </p>

            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div>
                    <label className="form-label" >Fecha</label>
                    <input type="date" className="form-control" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                </div>
                <div style={{ flex: '1 1 260px' }}>
                    <label className="form-label" >Motivo</label>
                    <input
                        type="text" className="form-control" maxLength={200}
                        placeholder="Feriado provincial, mudanza, capacitación…"
                        value={motivo} onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>
                <button className="btn btn-primary" onClick={agregar} disabled={guardandoDia} style={{ fontWeight: 600 }}>
                    {guardandoDia ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-plus-lg me-2" />}
                    Marcar día
                </button>
            </div>

            {errorDia && <div className="alert alert-danger py-2">{errorDia}</div>}
            {avisoTurnos && <div className="alert alert-warning py-2">{avisoTurnos}</div>}

            {dias.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>No hay días marcados de hoy en adelante.</p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ fontSize: '0.85rem' }}>Fecha</th>
                                <th style={{ fontSize: '0.85rem' }}>Motivo</th>
                                <th style={{ fontSize: '0.85rem' }}>Turnos ya agendados</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {dias.map((d) => (
                                <tr key={d.id}>
                                    <td style={{ fontSize: '0.9rem' }}>{d.fecha}</td>
                                    <td style={{ fontSize: '0.9rem' }}>{d.motivo}</td>
                                    <td style={{ fontSize: '0.9rem' }}>
                                        {d.turnosAgendados > 0
                                            ? <span className="text-warning-emphasis fw-semibold">{d.turnosAgendados} — hay que avisarles</span>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td className="text-end">
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => reabrir(d.id)}>
                                            Reabrir
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Importación masiva (una sola vez) del turnero histórico desde el CSV exportado de Google Sheets.
// Flujo en dos pasos: primero "Simular" (no persiste, muestra previsualización) y luego
// "Confirmar importación" (crea los turnos). Ver ImportacionTurnosService en el backend.
function PanelImportacionTurnos() {
    const [archivo, setArchivo] = useState(null);
    const [procesando, setProcesando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [errorImp, setErrorImp] = useState('');
    const [confirmado, setConfirmado] = useState(false);

    const handleArchivo = (e) => {
        setArchivo(e.target.files?.[0] || null);
        setResultado(null);
        setErrorImp('');
        setConfirmado(false);
    };

    const ejecutar = async (simulacion) => {
        if (!archivo) {
            setErrorImp('Elegí primero el archivo CSV.');
            return;
        }
        setErrorImp('');
        setProcesando(true);
        try {
            const data = await importarTurnosCsv(archivo, simulacion);
            setResultado(data);
            if (!simulacion) setConfirmado(true);
        } catch (e) {
            setErrorImp(e?.response?.data?.mensaje || 'Error al procesar el archivo.');
        } finally {
            setProcesando(false);
        }
    };

    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginTop: 24, boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ margin: '0 0 4px', fontWeight: 700, color: '#1e293b', fontSize: '1.15rem' }}>
                <i className="bi bi-upload me-2" style={{ color: '#3b82f6' }} />
                Importar turnero histórico (CSV)
            </h3>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.88rem' }}>
                Cargá el CSV exportado de la planilla del turnero. Primero <strong>simulá</strong> para
                revisar cómo quedaría el mapeo (nombre/apellido, estado, teléfono) y recién después
                confirmá la importación. No se guardan DNI ni CUIL.
            </p>

            <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleArchivo}
                className="form-control mb-3"
                style={{ maxWidth: 480 }}
            />

            <div className="d-flex gap-2 flex-wrap mb-3">
                <button
                    className="btn btn-outline-primary"
                    onClick={() => ejecutar(true)}
                    disabled={procesando || !archivo}
                    style={{ fontWeight: 600 }}
                >
                    {procesando
                        ? <><span className="spinner-border spinner-border-sm me-2" />Procesando…</>
                        : <><i className="bi bi-search me-2" />Simular</>}
                </button>
                <button
                    className="btn btn-primary"
                    onClick={() => ejecutar(false)}
                    disabled={procesando || !archivo || !resultado || confirmado}
                    style={{ fontWeight: 600 }}
                    title={!resultado ? 'Primero simulá para revisar' : ''}
                >
                    <i className="bi bi-check-lg me-2" />Confirmar importación
                </button>
            </div>

            {errorImp && <div className="alert alert-danger py-2">{errorImp}</div>}

            {resultado && (
                <div>
                    <div
                        className={`alert ${resultado.simulacion ? 'alert-info' : 'alert-success'} py-2`}
                        style={{ fontSize: '0.9rem' }}
                    >
                        <strong>
                            {resultado.simulacion
                                ? 'Simulación (no se guardó nada).'
                                : '¡Importación realizada!'}
                        </strong>{' '}
                        {resultado.simulacion ? 'Se crearían' : 'Se crearon'}{' '}
                        <strong>{resultado.importados}</strong> turnos nuevos
                        {' '}· <strong>{resultado.actualizados}</strong> existentes corregidos (tipo/carpeta)
                        {' '}· leídas <strong>{resultado.filasTotales}</strong> filas
                        {' '}· <strong>{resultado.duplicados}</strong> duplicados en el archivo
                        {' '}· <strong>{resultado.omitidosSinDatos}</strong> sin datos válidos.
                        <div style={{ marginTop: 4, fontSize: '0.85rem' }}>
                            Áreas detectadas: <strong>{resultado.areaLaboral}</strong> Laboral (ART),{' '}
                            <strong>{resultado.areaCobros}</strong> Cobros, resto Atención al Público.{' '}
                            Canal bot: <strong>{resultado.canalBot}</strong>.
                            {resultado.omitidosAnses > 0 && (
                                <> · <strong>{resultado.omitidosAnses}</strong> filas ANSES omitidas
                                (se cargan desde el archivo de turnos ANSES).</>
                            )}
                        </div>
                    </div>

                    {resultado.filasAnsesOmitidas?.length > 0 && (
                        <details style={{ marginBottom: 12 }} open>
                            <summary style={{ cursor: 'pointer', color: '#b45309', fontSize: '0.88rem', fontWeight: 700 }}>
                                ⚠️ {resultado.filasAnsesOmitidas.length} turnos ANSES omitidos — verificá que estén en el archivo de turnos ANSES
                            </summary>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', margin: '6px 0' }}>
                                Estos se cargan desde el archivo ANSES (que tiene CUIL/UDAI). Revisá esta lista contra esa carga: si alguno no está (huérfano), cargalo a mano.
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-sm table-bordered" style={{ fontSize: '0.8rem' }}>
                                    <thead style={{ background: '#fff7ed' }}>
                                        <tr><th>Fecha</th><th>Hora</th><th>Nombre</th><th>Detalle</th></tr>
                                    </thead>
                                    <tbody>
                                        {resultado.filasAnsesOmitidas.map((f, i) => (
                                            <tr key={i}>
                                                <td>{f.fecha}</td>
                                                <td>{f.horario}</td>
                                                <td>{f.nombre}</td>
                                                <td>{f.detalle}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}

                    {resultado.referenciasDetectadas?.length > 0 && (
                        <details style={{ marginBottom: 12 }} open>
                            <summary style={{ cursor: 'pointer', color: '#1d4ed8', fontSize: '0.88rem', fontWeight: 700 }}>
                                Referencias encontradas ({resultado.referenciasDetectadas.length}) — se preservan en la observación
                            </summary>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', margin: '6px 0' }}>
                                Todavía no se mapean al catálogo (eso será un paso de confirmación aparte). Verde = coincide con una referencia ya creada.
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                                    <thead style={{ background: '#f1f5f9' }}>
                                        <tr><th>Valor en la planilla</th><th style={{ width: 70 }}>Veces</th><th>Catálogo</th></tr>
                                    </thead>
                                    <tbody>
                                        {resultado.referenciasDetectadas.map((r, i) => (
                                            <tr key={i}>
                                                <td>{r.valor}</td>
                                                <td>{r.cantidad}</td>
                                                <td style={{ color: r.catalogo ? '#166534' : '#b45309' }}>
                                                    {r.catalogo || 'sin mapear'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}

                    {resultado.muestra?.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 6 }}>
                                Muestra de los primeros {resultado.muestra.length} turnos (revisá el corte nombre/apellido):
                            </div>
                            <table className="table table-sm table-bordered" style={{ fontSize: '0.82rem' }}>
                                <thead style={{ background: '#f1f5f9' }}>
                                    <tr>
                                        <th>Fecha</th><th>Hora</th><th>Apellido</th>
                                        <th>Nombre</th><th>Teléfono</th><th>Estado</th>
                                        <th>Área</th><th>Canal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultado.muestra.map((f, i) => (
                                        <tr key={i}>
                                            <td>{f.fecha}</td>
                                            <td>{f.horario}</td>
                                            <td>{f.apellido}</td>
                                            <td>{f.nombre}</td>
                                            <td>{f.telefono || '—'}</td>
                                            <td>{f.estado}</td>
                                            <td>{f.area}</td>
                                            <td>{f.canal === 'CHATBOT_EXTERNO' ? 'bot' : 'import'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {resultado.advertencias?.length > 0 && (
                        <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', color: '#b45309', fontSize: '0.85rem', fontWeight: 600 }}>
                                {resultado.advertencias.length} advertencia(s)
                            </summary>
                            <ul style={{ fontSize: '0.82rem', color: '#78716c', marginTop: 6 }}>
                                {resultado.advertencias.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}
